/**
 * useInsights – unified hook that produces predictions, recommendations,
 * and alerts for the current screen. Internally reuses
 * `useRecommendations` (which already handles UIContext extraction +
 * route + DOM observation).
 *
 * Triggers:
 *   - on page load (via useRecommendations effect)
 *   - on data change (MutationObserver inside useRecommendations)
 *   - on route change
 *   - optional polling every 30 seconds (off by default)
 */

import { useEffect, useMemo, useState } from 'react';
import { useRecommendations } from './useRecommendations';
import { generatePredictions, type Prediction } from '../services/PredictiveEngine';
import {
  alertEngine,
  type AlertEvent,
} from '../services/AlertEngineService';

export interface UseInsightsOptions {
  /** Re-evaluate on a fixed interval (ms). Set 0 to disable. */
  pollIntervalMs?: number;
}

export interface UseInsightsResult {
  predictions: Prediction[];
  recommendations: ReturnType<typeof useRecommendations>['recommendations'];
  alerts: AlertEvent[];
  refresh: () => void;
}

export function useInsights(options: UseInsightsOptions = {}): UseInsightsResult {
  const { context, recommendations, refresh } = useRecommendations();
  const { pollIntervalMs = 0 } = options;

  const predictions = useMemo(() => generatePredictions(context), [context]);

  const [alerts, setAlerts] = useState<AlertEvent[]>(() => alertEngine.getActive());

  // Re-evaluate alerts whenever the context changes.
  useEffect(() => {
    const evts = alertEngine.evaluateAlerts(context);
    setAlerts(evts);
  }, [context]);

  // Subscribe to dismissals etc.
  useEffect(() => {
    return alertEngine.subscribe(setAlerts);
  }, []);

  // Optional polling.
  useEffect(() => {
    if (!pollIntervalMs || pollIntervalMs <= 0) return;
    const id = window.setInterval(refresh, pollIntervalMs);
    return () => window.clearInterval(id);
  }, [pollIntervalMs, refresh]);

  return { predictions, recommendations, alerts, refresh };
}
